import React, { useEffect, useState, useRef } from "react";
import { Modal } from "react-bootstrap";
import { Skeleton } from "@mui/material";

import styles from "./home.module.css";

import Card from "../components/card/Card";
import Sidebar from "../components/sidebar/sidebar";
import AccountUnknown from "../../AccountUnknown";
import AccountPending from "../../AccountPending";
import AccountNotApproved from "../../AccountNotApproved";

import { useCookies } from "../../../hooks/useCookies";
import * as profileAction from "../../../actions/Profile";
import { useUserProfile } from "../components/context/UserProfileContext";
import { useProfile } from "../../userflow/components/context/ProfileContext";
import { useNavigate } from "react-router-dom";
import Loader from "../../Utility/Loader";
import { InstallApp } from "../../InstallApp/InstallApp";
import { handleUserNavigation, checkProfileCompleteness } from "../../../utils/handleUserRedirect";

export default function Home() {
  const [approval, setApproval] = useState("");
  const [approvalloading, setApprovalloading] = useState(false);
  const [showUpdateReject, setShowUpdateReject] = useState(false);
  const [rejectreason, setRejectReason] = useState("");

  const hasFetchedApproval = useRef(false);
  const hasFetchedRejectionReason = useRef(false);
  const observerRef = useRef(null);

  const { getCookie, clearAllCookies } = useCookies();
  const userId = getCookie("userId");
  const { profiles, setPage, loading } = useUserProfile();
  const { profile, fetchProfile } = useProfile();
  const navigate = useNavigate();
  const [profileUpdate, setProfileUpdate] = useState();
  const [loader, setLoading] = useState(false);
  const [isProfileIncomplete, setIsProfileIncomplete] = useState(false);

  // ✅ Fetch rejection reason if applicable
  const fetchRejectionReason = async () => {
    if (hasFetchedRejectionReason.current) return;
    hasFetchedRejectionReason.current = true;

    const rejectedStatus = await profileAction.getResponse(
      "customer/users/UpdateRejectReason"
    );
    if (rejectedStatus?.status) {
      setRejectReason(rejectedStatus.reason);
      setShowUpdateReject(true);
    }
  };

  // ✅ Delete rejection reason
  const handleFinalDelete = async () => {
    const rejectStatus = await profileAction.deleteLatestRejectReason();
    if (rejectStatus) {
      setShowUpdateReject(false);
      setRejectReason("");
    } else {
      console.error(
        "Error deleting rejection reason:",
        rejectStatus?.message || "Unknown error"
      );
    }
  };

  // ✅ Fetch user approval status
  useEffect(() => {
    const fetchData = async () => {
      if (hasFetchedApproval.current || !userId) return;
      hasFetchedApproval.current = true;

      setApprovalloading(true);
      try {
        const checkStatus = await profileAction.CheckUserApproval(userId);
        if (checkStatus) {
          setApproval(checkStatus.approval);

          if (
            checkStatus.approval === 20 &&
            !hasFetchedRejectionReason.current
          ) {
            await fetchRejectionReason();
          }
        }
      } catch (err) {
        console.error("Approval fetch failed:", err);
      } finally {
        setApprovalloading(false);
      }
    };

    fetchData();
  }, [userId]);

  // ✅ Animate cards when they come into view
  useEffect(() => {
    if (profiles?.length === 0) return;

    if (observerRef.current) observerRef.current.disconnect();

    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (
            !entry.target.classList.contains("show") &&
            entry.isIntersecting
          ) {
            entry.target.classList.add("show");
            observerRef.current?.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.2,
        root: document.querySelector(".children"),
      }
    );

    document.querySelectorAll(".card-container").forEach((card) => {
      observerRef.current?.observe(card);
    });

    return () => observerRef.current?.disconnect();
  }, [profiles]);

  useEffect(() => {
    const handleRouting = async () => {
      try {
        const success = await fetchProfile();
        setProfileUpdate(success?.data);
      } catch (error) {
        console.error("Error fetching profile:", error);
      }
    };
    if (!profile) {
      handleRouting();
    } else {
      setProfileUpdate(profile);
    }
  }, [profile]);

  // ✅ Handle incomplete profiles: redirect to relevant onboarding screens
  // ✅ Handle incomplete profiles: Show message instead of redirecting
  useEffect(() => {
    if (!profileUpdate) return;
    
    // Always check for basic email verification first
    if (profileUpdate?.Email == "" || !profileUpdate?.Email) {
      navigate("/emailverify");
      return;
    }

    // Check completeness
    const status = checkProfileCompleteness(profileUpdate);
    
    if (!status.complete) {
      setIsProfileIncomplete(true);
      setLoading(false); // Stop loading since we are staying on Home
    } else {
       setIsProfileIncomplete(false);
       // If complete, we optionally check approval or just let them be on Home
       // The original logic checked approval here too, but approval is handled by the render logic below.
       setLoading(false);
    }

    // Checking handleUserNavigation for legacy? We are replacing it.
    // handleUserNavigation(profileUpdate, navigate, setLoading, "home");
  }, [profileUpdate, navigate]);

  // ⛔ Redirect if user is marked as deleted
  useEffect(() => {
    if (profileUpdate?.Deleted) {
      console.warn("User profile is marked as deleted. Redirecting to home.");
      clearAllCookies();
      navigate("/");
    }
  }, [profileUpdate, clearAllCookies, navigate]);

  if (loading) {
    return <Loader />;
  }
  return (
    <Sidebar>
      <InstallApp isFloating={true} />
      {approvalloading ? (
        <Skeleton
          variant="rounded"
          width={400}
          height={900}
          style={{ marginBlock: "20px", borderRadius: "0.75rem" }}
        />
      ) : (
        <>



          {approval === 10 && <AccountPending />}
          {approval === 20 && (
            <>
              {profiles?.length === 0 ? (
                <div className={styles.emptyState}>
                  <h4 className={styles.emptyStateTitle}>Expand Your Search</h4>

                  <p className={styles.emptyStateText}>
                    Please update your Preferences to discover more users!
                  </p>

                  <button
                    className={styles.emptyStateButton}
                    onClick={() => navigate("/user/preferences")}
                  >
                    Update Preferences
                  </button>
                </div>
              ) : (
                <div className={styles.container}>
                  {profiles?.slice(0, 1)?.map((profile) => (
                    <Card
                      key={profile.user_id}
                      {...profile}
                      setPage={setPage}
                      show
                      showIncompleteBanner={isProfileIncomplete}
                    />
                  ))}
                  {loading && (
                    <Skeleton
                      variant="rounded"
                      width={400}
                      height={900}
                      style={{ marginBlock: "20px", borderRadius: "0.75rem" }}
                    />
                  )}
                </div>
              )}
            </>
          )}

          {approval === 30 && <AccountNotApproved />}
          {approval === 40 && <AccountUnknown />}
        </>
      )}
      <Modal
        className="customer-modal-without-header-footer"
        show={showUpdateReject}
        onHide={handleFinalDelete}
        centered
      >
        <Modal.Body className="pause-modal-content">
          <div className="pause-modal-title">Your Update Needs Attention</div>
          <div className="pause-modal-message">{rejectreason}</div>
          <div
            className="d-flex justify-content-center"
            style={{ gap: "30px" }}
          >
            <button className="global-save-button" onClick={handleFinalDelete}>
              Okay
            </button>
          </div>
        </Modal.Body>
      </Modal>
    </Sidebar>
  );
}
